import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ChunkLoadBoundary, Spinner, ToastProvider } from '@mbbe/ui';
import { OpsAuthProvider, useOpsAuth } from './OpsAuthContext';
import { ContinuationPrompt } from './components/ContinuationPrompt';

const Login = React.lazy(() => import('./pages/Login'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Tasks = React.lazy(() => import('./pages/Tasks'));
const TaskDetail = React.lazy(() => import('./pages/TaskDetail'));
const Earnings = React.lazy(() => import('./pages/Earnings'));
const OffDays = React.lazy(() => import('./pages/OffDays'));
// Staff project workspace (roadmap, notes, discussions); docs/platform-expansion-20261015.md.
const OpsProjectRoutes = React.lazy(() => import('./features/projects/OpsProjectRoutes'));

function Protected({ children }: { children: React.ReactNode }) {
  const { user, isStaff, isLoading } = useOpsAuth();
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-900">
        <Spinner size="lg" className="text-brand-500" />
      </div>
    );
  }
  if (!user || !isStaff) return <Navigate to="/login" replace />;
  return (
    <>
      {children}
      <ContinuationPrompt />
    </>
  );
}

const loadingFallback = (
  <div className="flex min-h-screen items-center justify-center bg-dark-900">
    <Spinner size="lg" className="text-brand-500" />
  </div>
);

export default function OpsApp() {
  return (
    <ToastProvider>
      <OpsAuthProvider>
        <BrowserRouter>
          <ChunkLoadBoundary>
            <React.Suspense fallback={loadingFallback}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/" element={<Protected><Dashboard /></Protected>} />
                <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
                <Route path="/tasks/:id" element={<Protected><TaskDetail /></Protected>} />
                <Route path="/projects/*" element={<Protected><OpsProjectRoutes /></Protected>} />
                <Route path="/off-days" element={<Protected><OffDays /></Protected>} />
                <Route path="/earnings" element={<Protected><Earnings /></Protected>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </React.Suspense>
          </ChunkLoadBoundary>
        </BrowserRouter>
      </OpsAuthProvider>
    </ToastProvider>
  );
}
